import { css } from 'styled-components';
import { transition } from './animate';
import brand from './brand';
import { fluid } from './fluid';
import fonts from './fonts';
import { Device, until } from './media';
export const headerLink = css `
  ${transition('color, text-decoration-color')};

  display: inline;
  color: currentColor;
  text-decoration: underline;
  text-decoration-color: ${brand.yellow};
  text-underline-offset: -0.19em;
  text-underline-position: under;

  @media ${until(Device.MobileLarge)} {
    word-break: break-word;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: auto;
    outline-width: thin;
  }

  &:focus,
  &:hover {
    text-decoration-color: currentColor; 
  }
`;
export const headingStyles = css `
  color: currentColor;
  ${fonts.altGothicNo2.regular};
  margin: 0 0 ${fluid(16, 24)};
  text-transform: uppercase;
  a{
    ${headerLink}
  }

`;
export const h1 = css `
  ${headingStyles};
  font-size: ${fluid(45, 96)};
  line-height: ${fluid(45, 96)};
`;
export const h2 = css `
  ${headingStyles};
  font-size: ${fluid(30, 56)};
  line-height: ${fluid(30, 54)};
`;
export const h3 = css `
  ${headingStyles};
  font-size: ${fluid(24, 30)};
  line-height: ${fluid(24, 30)};
`;
export const h4 = css `
  ${headingStyles};
  font-size: ${fluid(18, 20)};
  line-height: ${fluid(18, 18)};
`;
export const h5 = css `
  ${headingStyles};
  font-size: ${fluid(18, 20)};
  line-height: ${fluid(18, 18)};
`;
export const h6 = css `
  ${headingStyles};
  font-size: ${fluid(18, 18)};
  line-height: ${fluid(22, 22)};
`;
export const paragraphStyles = css `
  color: currentColor;
  ${fonts.apercu.light};
  margin: 0 0 ${fluid(16, 24)};
`;
export const paragraphLarge = css `
  ${paragraphStyles};
  font-size: ${fluid(18, 24)};
  line-height: ${fluid(28, 32)};
`;
export const paragraphMedium = css `
  ${paragraphStyles};
  font-size: ${fluid(16, 18)};
  line-height: ${fluid(26, 26)};
`;
export const paragraphSmall = css `
  ${paragraphStyles};
  font-size: ${fluid(14, 16)};
  line-height: ${fluid(24, 24)};
`;
export const paragraphMicro = css `
  ${paragraphStyles};
  font-size: ${fluid(12, 14)};
  line-height: ${fluid(22, 24)};
`;
export const meta = css `
  display: block;
  ${fonts.altGothicNo2.regular};
  font-size: ${fluid(18, 18)};
  line-height: ${fluid(22, 22)};
  letter-spacing: 0.01em;
  text-transform: uppercase;

  sup {
    font-size: 60%;
  }
`;
export const link = css `
  ${transition('color, text-decoration-color')};

  display: inline;
  color: currentColor;
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-underline-position: under;

  @media ${until(Device.MobileLarge)} {
    word-break: break-word;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: auto;
    outline-width: thin;
  }

  &:focus,
  &:hover {
    text-decoration-color: ${brand.yellow};
  }
`;
export const table = css `
  background: ${brand.white};
  border-collapse: collapse;
  color: ${brand.black};
  margin-bottom: 1em;
  width: 100%;

  thead {
    tr {
      background: ${brand.black};

      td,
      th {
        border: 2px solid ${brand.white};
        color: ${brand.white};
        ${fonts.altGothicNo2.regular};
        font-size: 18px;
        letter-spacing: -0.01em;
        line-height: 22px;
        padding: 12px 16px;
        text-align: left;
        text-transform: uppercase;
      }
    }
  }

  tbody {
    tr {
      &:nth-child(odd) {
        background: ${brand.white};
      }

      &:nth-child(even) {
        background: ${brand.white};
      }

      th {
        border: 2px solid ${brand.white};
        color: ${brand.white};
        ${fonts.altGothicNo2.regular};
        font-size: 18px;
        letter-spacing: -0.01em;
        line-height: 22px;
        padding: 12px 16px;
        text-align: left;
        text-transform: uppercase;
      }

      td {
        border: 2px solid ${brand.white};
        ${fonts.apercu.light};
        font-size: 16px;
        letter-spacing: -0.01em;
        line-height: 20px;
        padding: 12px 16px;
        text-align: left;
      }
    }
  }
  @media ${until(Device.TabletSmall)} {
    padding: 4px;
  }
`;
export const blockquote = css `
  ${paragraphLarge}

  position: relative;
  border-left: 4px solid currentColor;
  padding-left: 16px;
  text-align: left;
`;
export const contentStyles = css `
  h1,
  .h1-styles {
    ${h1}
  }

  h2,
  .h2-styles {
    ${h2}
  }

  h3,
  .h3-styles {
    ${h3}
  }

  h4,
  .h4-styles {
    ${h4}
  }

  h5,
  .h5-styles {
    ${h5}
  }

  p {
    ${paragraphLarge}
  }

  p.small {
    ${paragraphSmall}
  }

  a {
    ${link}
  }

  ol,
  ul {
    ${paragraphLarge};

    li {
      &::marker {
        color: ${brand.yellow};
      }
    }
  }

  ol {
    list-style: decimal;
    padding-left: 1.2em;
  }

  ul {
    list-style: disc;
    padding-left: 1.2em;
  }

  hr {
    display: block;
    background: currentColor;
    border: 0;
    height: 4px;
    margin: ${fluid(40, 80)} 0;
    padding: 0;
    width: 100%;
  }

  table {
    ${table};
  }

  blockquote {
    ${blockquote};
  }

  .responsive-table {
    position: relative;
    overflow-x: auto;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;
export const contentStylesSmall = css `
  h1,
  .h1-styles {
    ${h1}
  }

  h2,
  .h2-styles {
    ${h2}
  }

  h3,
  .h3-styles {
    ${h3}
  }

  h4,
  .h4-styles {
    ${h4}
  }

  h5,
  .h5-styles {
    ${h5}
  }

  p {
    ${paragraphMedium}
  }

  p.small {
    ${paragraphSmall}
  }

  a {
    ${link}
  }

  ol,
  ul {
    ${paragraphMedium};

    li {
      &::marker {
        color: ${brand.yellow};
      }
    }
  }

  ol {
    list-style: decimal;
    padding-left: 1.2em;
  }

  ul {
    list-style: disc;
    padding-left: 1.2em;
  }

  hr {
    display: block;
    background: currentColor;
    border: 0;
    height: 4px;
    margin: ${fluid(40, 80)} 0;
    padding: 0;
    width: 100%;
  }

  table {
    ${table};
  }

  blockquote {
    ${blockquote};
  }

  .responsive-table {
    position: relative;
    overflow-x: auto;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;
